/**
 * A module for integrating Rocketship applications with various telemetry/web metrics providers,
 * such as Urchin and Google Analytics. For a complete list of the available provider plugins,
 * see visit the [getanalytics]{@link https://getanalytics.io/plugins/#supported-analytic-tools} docs.
 * @module rs-analytics
 */

/**
 * Analytics definition ([davidwells/analytics]{@link https://github.com/DavidWells/analytics})
 * @typedef {Object} analytics
 * @property {Function} track - method used to track an event, see [analytics#track()]{@link https://getanalytics.io/api/#analyticstrack}
 * @property {Function} page - method used to track a page view, see [analytics#track()]{@link https://getanalytics.io/api/#analyticspage}
 */

import analyticsModule from 'analytics';
import rocketshipDataPlugin from './analytics-plugins/rocketship-data-analytics-plugin';
import urchinPlugin from './analytics-plugins/urchin-analytics-plugin';

export {
    rocketshipDataPlugin,
    urchinPlugin,
    determineChannel,
};

export { default as VuePlugin } from './vue-plugin';

export default createAnalytics;
/**
 * @alias module:rs-analytics
 * @param {Object} options - Module options
 * @param {{
 *  deployPath: string,
 *  pageName: string,
 *  affiliateId: string,
 *  isLoggedIn: boolean,
 * }} options.reactiveData Vue Reactive object containing properties for rocketshipDataPlugin.
 * @param {*} options.store Deprecated. Vuex store from Rocketship application.
 * @param {Array} options.plugins Custom list of middleware plugins for davidwells/analytics.
 * This overrides core plugins, so if you still need those, you will need to manually import
 * each and include them in the list.
 * @returns {import('analytics').AnalyticsInstance} instance of [davidwells/analytics]{@link https://github.com/DavidWells/analytics}
 */
function createAnalytics ({ reactiveData, store, plugins }) {
    return analyticsModule({
        app: process.env.VUE_APP_PACKAGE_NAME,
        // Urchin plugin needs Vue in order to inject stuff into the app
        // May also need this to access state from the store?
        plugins: plugins || [
            rocketshipDataPlugin({ reactiveData, store }),
            urchinPlugin({ reactiveData }),
        ],
        debug: true,
    });
}

// TODO: update launchpad to use this function for reg/login channel
function determineChannel () {
    // http://stackoverflow.com/a/8876069/575849
    return Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0,
    ) < 768 ?
        'm' :
        'def';
}
